import { TableRowModel } from '@examdojo/supabase';
import { EntityState } from '@examdojo/state';

export type ImageChatMessageHttpModelMessage = Array<{
  type: 'text' | 'image' | 'image_url';
  content: string;
}>;

export interface ImageChatMessageHttpModelMetadata {
  contains_image_code?: boolean;
}

export type ImageChatMessageHttpModel = Omit<TableRowModel<'llm_chat_messages'>, 'message' | 'metadata'> & {
  message: ImageChatMessageHttpModelMessage;
  metadata?: ImageChatMessageHttpModelMetadata;
};

export enum ImageChatMessageOrigin {
  Local = 'local',
  Remote = 'remote',
}

export interface ImageChatMessageContentStoreModel {
  text: string;
  images: string[];
}

interface BaseImageChatMessage
  extends Omit<
    ImageChatMessageHttpModel,
    'output_tokens' | 'stop_reason' | 'input_tokens' | 'message' | 'responds_to' | 'author'
  > {
  message: ImageChatMessageContentStoreModel;
  origin?: ImageChatMessageOrigin;
}

export interface UserImageChatMessageStoreModel extends BaseImageChatMessage {
  created_by_role: 'user';
}

export interface AgentRemoteImageChatMessageStoreModel extends BaseImageChatMessage {
  created_by_role: 'ai';
  origin: ImageChatMessageOrigin.Remote;
}

export type ImageChatMessageStoreModel = UserImageChatMessageStoreModel | AgentRemoteImageChatMessageStoreModel;

export type UserImageChatMessageUIModel = UserImageChatMessageStoreModel;

export type AgentRemoteImageChatMessageUIModel = AgentRemoteImageChatMessageStoreModel;

export type ImageChatMessageUIModel = UserImageChatMessageUIModel | AgentRemoteImageChatMessageUIModel;

export interface ImageIndexToChatIdProps {
  chatId: number;
  requestId: string | null;
}

export interface ImageChatMessageState
  extends EntityState<
    ImageChatMessageStoreModel,
    {
      reviewableItemId: string | null;
      imageIndexToChatId: Record<string, ImageIndexToChatIdProps>;
    },
    ImageChatMessageStoreModel['id']
  > {}

export function createUserMessageImageChatStoreModel(
  message: ImageChatMessageContentStoreModel,
  llm_chat_id: number,
): UserImageChatMessageStoreModel {
  const id = -Math.floor(Math.random() * 999999);
  return {
    id,
    message,
    created_at: `${new Date().getTime()}`,
    created_by_role: 'user',
    llm_chat_id,
    origin: ImageChatMessageOrigin.Local,
  };
}

export function mapImageChatMessageTableRowToStoreModel(
  model: TableRowModel<'llm_chat_messages'>,
): ImageChatMessageStoreModel {
  const httpModel = model as unknown as ImageChatMessageHttpModel;

  return {
    id: httpModel.id,
    message: {
      text: (httpModel.message ?? []).find((m) => m.type === 'text')?.content || '',
      images: (httpModel.message ?? []).filter((m) => m.type === 'image').map((m) => m.content),
    },
    created_at: httpModel.created_at,
    created_by_role: httpModel.created_by_role,
    origin: ImageChatMessageOrigin.Remote,
    llm_chat_id: httpModel.llm_chat_id,
    metadata: httpModel.metadata as ImageChatMessageHttpModelMetadata,
  };
}
